import {
  Box,
  Button,
  Divider,
  Drawer,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { IconX } from '@tabler/icons-react';
// import SelectContributionCore from './ContributionCore';
// import ContributionDetailsForm from './ContributionDetailsForm';
// import contributionCoreConfigs, { VirtualCore } from './ContributionCoreConfig';
import { SelectedValidator, Virtual } from '../../../../interfaces';
import { useQuery } from '@tanstack/react-query';
import { getVirtual } from '../../../../api/virtual.api';
import { getValidatorsByVirtual } from '../../../../api/validator.api';
import ValidatorTable from './ValidatorTable';
import VirtualBalanceChip from '../../../../components/common/VirtualBalanceChip';
// import { Contribution } from '../../../../api/contribution.api';
import { virtualDetailModalStyle } from '../../../../components/virtual/VirtualDetailModal.style';
import LoadingButton from '../../../../components/common/LoadingButton';
import { BigNumberish, ethers } from 'ethers';
import { useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import {
  activeChain,
  checkNetwork,
} from '../../../../shared/utils/CheckNetwork';
import { enqueueSnackbar } from 'notistack';
import { personaTokenABI } from '../../../../assets/abis/personaToken';
import {
  readContract,
  waitForTransactionReceipt,
  writeContract,
} from 'wagmi/actions';
import { config } from '../../../../shared/constants/WagmiConfig';
import { virtualTokenABI } from '../../../../assets/abis/virtualToken';
import { usePrivy } from '@privy-io/react-auth';
import { renderContractError } from '../../../../shared/utils/RenderContractError';
import { useVirtualBalanceContext } from '../../../../context/VirtualBalanceContext';
import { virtualDataset } from '../../../../assets/data/virtualDataset';

const WithdrawDrawer: React.FC<{
  isOpen: boolean;
  virtual: Virtual;
  onClose: () => void;
  totalDelegate: BigInt;
}> = ({ isOpen, onClose, virtual, totalDelegate }) => {
  // const [selectedCore, setSelectedCore] = useState<VirtualCore | null>(null);
  const [canClose, setCanClose] = useState(true);
  const [validatorQuery, setValidatorQuery] = useState<string>('');
  const [selectedValidator, setSelectedValidator] = useState<
    SelectedValidator | undefined
  >();
  const [withdrawAmount, setWithdrawAmount] = useState<Number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canWithdraw, setCanWithdraw] = useState<boolean>(false);
  const [stakeData, setStakeData] = useState<`0x${string}` | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>();

  const { xVirtualBalance } = useVirtualBalanceContext();
  const [virtualTokenAddress, setVirtualTokenAddress] = useState<string>('');

  const virtualData = useQuery({
    queryKey: ['virtual', virtual.id],
    queryFn: () => getVirtual(virtual.id),
    enabled: virtual.id > 0,
  });

  const { data: validators } = useQuery({
    queryKey: ['validator', virtual.id, validatorQuery],
    queryFn: () =>
      getValidatorsByVirtual({
        virtualId: virtual.id,
        name: validatorQuery,
        address: user?.wallet?.address,
      }),
    enabled: !!virtual.id,
  });

  const { writeContractAsync } = useWriteContract({
    mutation: {
      onSuccess(data) {
        // On success, wait for tx approved before closing modal
        enqueueSnackbar({
          key: data,
          message: `Transaction is in progress`,
          action: (
            <a
              rel="noreferrer"
              target="_blank"
              href={`${activeChain.blockExplorers.default.url}/tx/${data}`}
            >
              <Button>View Tx</Button>
            </a>
          ),
        });

        setStakeData(data);
      },
      onError(error) {
        const errorMessage = renderContractError(error);
        setErrorMessage(errorMessage);
      },
    },
  });

  const { isSuccess: isTxConfirmed, isLoading: isTxConfirming } =
    useWaitForTransactionReceipt({ hash: stakeData });

  const validateWithdrawAmount = (value: string) => {
    if (value && !isNaN(Number(value)) && Number(value) > 0) {
      setWithdrawAmount(Number(value));
      if (Number(value) <= Number(totalDelegate)) {
        setCanWithdraw(true);
      } else {
        setCanWithdraw(false);
      }
    } else {
      setWithdrawAmount(0);
      setCanWithdraw(false);
    }
  };

  // Allow user to perform withdraw on selected virtual
  const withdraw = async () => {
    try {
      // Ensure correct chain
      await checkNetwork();

      if (
        withdrawAmount &&
        Number(withdrawAmount) >
          Number(ethers.formatUnits(totalDelegate as BigNumberish))
      ) {
        enqueueSnackbar({
          message: 'Stake amount exceeds $xVIRTUAL balance',
          variant: 'error',
        });
        throw new Error();
      }

      // Perform stake
      if (withdrawAmount) {
        setIsLoading(true);

        await writeContractAsync({
          chainId: activeChain.id,
          abi: personaTokenABI,
          address: virtualTokenAddress as `0x${string}`,
          functionName: 'withdraw',
          args: [BigInt(ethers.parseUnits(withdrawAmount.toString(), 'ether'))],
        });
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const { authenticated, user, login } = usePrivy();

  const getVirtualTokenAddress = (virtualId: string, tokenAddress: string) => {
    const virtual = virtualDataset.data.find(
      (d: { virtualId: string; token: string }) => d.virtualId === virtualId
    );
    if (virtual) {
      return virtual.token;
    } else {
      return tokenAddress;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setWithdrawAmount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (virtualData?.data) {
      setVirtualTokenAddress(
        getVirtualTokenAddress(
          virtualData.data.virtualId.toString(),
          virtualData.data.tokenAddress
        )
      );
    }
  }, [virtualData]);

  useEffect(() => {
    validateWithdrawAmount(withdrawAmount.toString());
  }, [withdrawAmount]);

  return (
    <Drawer
      anchor={'right'}
      open={isOpen}
      onClose={() => canClose && onClose()}
    >
      <Stack
        height={'100%'}
        sx={{
          width: {
            xs: '100vw',
            sm: '75vw',
            md: '50vw',
            lg: '35vw',
          },
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Stack>
          <Stack
            m={2}
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Typography variant="h6" fontWeight={'bold'}>
              Withdraw from {virtual?.name}
            </Typography>

            <IconX onClick={() => canClose && onClose()} cursor={'pointer'} />
          </Stack>

          <img
            style={{ width: '100%', height: '250px', objectFit: 'cover' }}
            src={virtual?.image?.url}
            alt={virtual?.name}
          />

          {virtualData.data && (
            <Box m={2}>
              <ValidatorTable
                tokenAddress={virtualTokenAddress}
                data={validators?.data}
                totalDelegate={totalDelegate}
                userAddress={user?.wallet?.address || ''}
                onRowClick={(value: BigInt) =>
                  setWithdrawAmount(
                    Number(ethers.formatUnits(value as BigNumberish))
                  )
                }
              />
            </Box>
          )}
        </Stack>
        <Box
          maxWidth={'100%'}
          m={2}
          sx={{
            width: 'auto',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            marginBottom=".5rem"
          >
            <Typography sx={{ color: '#404040' }}>
              Amount to Withdraw
            </Typography>
            <VirtualBalanceChip value={totalDelegate} />
          </Box>

          <TextField
            size="small"
            sx={virtualDetailModalStyle.inputStakeAmount}
            fullWidth
            value={Number(withdrawAmount) > 0 ? withdrawAmount.toString() : ''}
            onChange={(e) => validateWithdrawAmount(e.target.value)}
            disabled={isLoading || isTxConfirming}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    disabled={isLoading || isTxConfirming}
                    sx={virtualDetailModalStyle.btnMax}
                    color="lightPrimary"
                    variant="contained"
                    onClick={() =>
                      setWithdrawAmount(
                        Number(
                          ethers.formatUnits(totalDelegate as BigNumberish)
                        )
                      )
                    }
                  >
                    Max
                  </Button>
                </InputAdornment>
              ),
            }}
          ></TextField>

          <LoadingButton
            disabled={
              !canWithdraw ||
              (withdrawAmount &&
                Number(withdrawAmount) >
                  Number(ethers.formatUnits(totalDelegate as BigNumberish)))
            }
            loading={isLoading || isTxConfirming}
            sx={virtualDetailModalStyle.btnStake}
            size="large"
            onClick={withdraw}
            variant="contained"
            fullWidth
          >
            Withdraw
          </LoadingButton>
          {errorMessage && errorMessage.length > 0 && (
            <Typography variant="body2" color="error" mt={1}>
              {errorMessage}
            </Typography>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
};

export default WithdrawDrawer;
